import { Box, HStack, useColorModeValue } from "@chakra-ui/react";
import { ReactNode } from "react";
import CDS from "design-system";

/**
 * Bordered, rounded-xl compose-box chrome shared between message + email
 * forms. Header rows render with the tinted header bg + a divider beneath;
 * the body is the caller's responsibility (they own the bg + min-height +
 * any focus/hover styles), and grabs colors from `useComposeBoxColors()`.
 *
 * Compound API:
 *   <ComposeBox>
 *     <ComposeBox.HeaderRow label="To">...</ComposeBox.HeaderRow>
 *     <ComposeBox.HeaderRow label="CC">...</ComposeBox.HeaderRow>
 *     <BodyBox>...</BodyBox>
 *   </ComposeBox>
 */
interface ComposeBoxProps {
  children: ReactNode;
}

function ComposeBox({ children }: ComposeBoxProps) {
  const { border } = useComposeBoxColors();
  // Don't add overflow:hidden — Trix popovers/dialogs at the box corners get
  // clipped. Round corners directly on first/last children instead.
  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="xl"
      sx={{
        "& > *:first-of-type": {
          borderTopLeftRadius: "xl",
          borderTopRightRadius: "xl",
        },
        "& > *:last-of-type": {
          borderBottomLeftRadius: "xl",
          borderBottomRightRadius: "xl",
        },
      }}
    >
      {children}
    </Box>
  );
}

interface HeaderRowProps {
  /** Label rendered to the left ("To", "From", "CC"). Omit for label-less rows like the subject line. */
  label?: string;
  children: ReactNode;
  /** Render this row without the bottom divider — use only for the last header before the body. */
  noDivider?: boolean;
  /** Override the row's background (e.g. plain body bg for the subject row). */
  bg?: string;
}

function HeaderRow({ label, children, noDivider = false, bg }: HeaderRowProps) {
  const { border, headerBg } = useComposeBoxColors();
  return (
    <HStack
      px={4}
      py={2}
      borderBottom={noDivider ? undefined : "1px solid"}
      borderColor={border}
      bg={bg ?? headerBg}
      spacing={2}
      align="center"
    >
      {label && (
        <CDS.Text12 fontWeight="medium" color="fg.muted" flexShrink={0} minW="40px">
          {label}
        </CDS.Text12>
      )}
      <Box flex={1} minW={0}>{children}</Box>
    </HStack>
  );
}

ComposeBox.HeaderRow = HeaderRow;

export default ComposeBox;

/** Color tokens shared between compose-box header, body, and hover state. */
export function useComposeBoxColors() {
  const border = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.900");
  const bodyBg = useColorModeValue("white", "gray.800");
  const bodyHoverBg = useColorModeValue("gray.50", "gray.700");
  return { border, headerBg, bodyBg, bodyHoverBg };
}

/** Strips Trix's default border/padding so the editor sits flush inside a
 *  compose body row, and hides nesting/undo/redo toolbar icons. Spread into
 *  the body row's `sx` alongside any per-form hover styles. */
export const BORDERLESS_TRIX_SX = {
  ".trix-container trix-editor": {
    border: "none !important",
    borderRadius: "0 !important",
    padding: "0 !important",
    minHeight: "120px",
  },
  ".trix-container": {
    gap: "8px !important",
  },
  ".trix-button--icon-decrease-nesting-level, .trix-button--icon-increase-nesting-level, .trix-button--icon-undo, .trix-button--icon-redo": {
    display: "none !important",
  },
};
